import { IS_TAURI } from './browser/globalEnvironment';
import { createCallbackManager } from './callbacks';
import { ESTABLISH_BROADCAST_CHANNEL_NAME } from './multiaccount';
import { getPasscodeHash, setPasscodeHash } from './passcode';

import Deferred from './Deferred';

const ESTABLISH_TIMEOUT = 100;
// How long a takeover waits for the current master to hand the role over before claiming it anyway.
const MASTER_HANDOVER_TIMEOUT = 3000;
// Nothing notices a master that stopped existing: a discarded (Chrome memory saver) or crashed tab
// sends no `tokenDied`, so the WebSocket goes with it and the remaining tabs sit on stale data — and
// the nudge they fire on becoming visible is itself a request to the dead master, so it hangs until
// its own 45s timeout. A visible tab that has heard nothing from the master for
// `MASTER_HEARTBEAT_TIMEOUT` pings it, and takes the role only if that goes unanswered.
const MASTER_HEARTBEAT_INTERVAL = 3000;
const MASTER_HEARTBEAT_TIMEOUT = 10000;
// How long the ping may go unanswered before the master counts as gone
const MASTER_PING_GRACE = 1500;
// A gap this much larger than the tick means the machine slept or the tab was frozen, not that the
// master died — every timestamp is stale, so re-arm rather than judge
const WALL_CLOCK_JUMP = 30000;
// The master holds a Web Lock named after its token for as long as it is master. An unanswered ping
// only means its main thread is busy (a heavy chat, a slow machine) when that lock is still held —
// taking over then made the role bounce between tabs, and each switch re-ran the whole startup. The
// browser releases the lock when the tab closes, crashes or is discarded, which is the real death.
const MASTER_LOCK_PREFIX = `${ESTABLISH_BROADCAST_CHANNEL_NAME}-master-`;
// A frozen tab keeps its locks too, so a master silent for this long is replaced even while holding it
const MASTER_BUSY_TOLERANCE = 30000;

const { addCallback, runCallbacks } = createCallbackManager();
const { addCallback: addCallbackTokenDied, runCallbacks: runCallbacksTokenDied } = createCallbackManager();
const token = Number(Math.random().toString().substring(2));
const collectedTokens = new Set([token]);
const channel = new BroadcastChannel(ESTABLISH_BROADCAST_CHANNEL_NAME);

let isEstablished = false;
const initialEstablishment = new Deferred();
let masterToken: number | undefined;
let isWaitingForMaster = false;
let reestablishToken: number | undefined;
let isChannelClosed = false;
let watchdogInterval: number | undefined;
let lastMasterHeartbeatAt = 0;
let lastWatchdogTickAt = 0;
let pendingPingAt = 0;
let wasMaster = false;
let masterLock: Deferred | undefined;

type EstablishMessage = {
  collectedTokens: Set<number>;
  masterToken?: number;
  tokenDied?: number;
  currentPasscodeHash?: ArrayBuffer;
  reestablishToken?: number;
  shouldGiveUpMaster?: boolean;
  hasGaveUpMaster?: boolean;
  claimedMaster?: number;
  masterPing?: number;
  masterHeartbeat?: number;
};

const handleMessage = ({ data }: { data: EstablishMessage }) => {
  if (!data) return;

  // A tab asking whether the master is still there. Only the master answers, and it answers from the
  // message handler — which the browser still delivers to a hidden tab whose TIMERS are throttled.
  if (data.masterPing !== undefined) {
    if (masterToken === token && !isChannelClosed) {
      channel.postMessage({ masterHeartbeat: token });
    }

    return;
  }

  if (data.masterHeartbeat !== undefined) {
    if (data.masterHeartbeat === masterToken) {
      lastMasterHeartbeatAt = Date.now();
      pendingPingAt = 0;
    }

    return;
  }

  if (data.currentPasscodeHash) {
    setPasscodeHash(data.currentPasscodeHash);
  }

  // A takeover winner announced itself. Reconcile deterministically — the higher token wins — so any
  // accidental split-brain (several tabs claiming master at once, or a stale/wedged master) collapses
  // to a single acting master. If we are the higher master, re-assert so the lower claimant demotes.
  if (data.claimedMaster !== undefined) {
    if (masterToken === token && token > data.claimedMaster) {
      channel.postMessage({ claimedMaster: token });
      return;
    }
    isWaitingForMaster = false;
    masterToken = data.claimedMaster;
    isEstablished = true;
    initialEstablishment.resolve();
    runCallbacks(masterToken === token);
    return;
  }

  if (data.hasGaveUpMaster && isWaitingForMaster) {
    claimMasterRole();
    return;
  }

  if (data.shouldGiveUpMaster) {
    if (masterToken === token) {
      runCallbacks(false);
      channel.postMessage({ currentPasscodeHash: getPasscodeHash(), hasGaveUpMaster: true });
    }
    masterToken = data.masterToken;
    return;
  }

  if (data.tokenDied) {
    runCallbacksTokenDied(data.tokenDied);
    collectedTokens.delete(data.tokenDied);
    if (data.tokenDied === masterToken) {
      collectedTokens.delete(data.tokenDied);
      masterToken = undefined;
      isEstablished = false;
      reestablishToken = data.tokenDied;

      channel.postMessage({
        collectedTokens,
        masterToken,
        reestablishToken,
      });
      if (collectedTokens.size === 1) {
        isEstablished = true;
        masterToken = token;
        reestablishToken = undefined;
        initialEstablishment.resolve();
        runCallbacks(true);
      }
    }
  }

  if (data.collectedTokens) {
    if (!data.reestablishToken && reestablishToken) {
      return;
    }
    if (data.reestablishToken && reestablishToken !== data.reestablishToken) {
      data.collectedTokens.delete(data.reestablishToken);

      reestablishToken = data.reestablishToken;
    }
    const prevLength = collectedTokens.size;
    data.collectedTokens.forEach((l) => collectedTokens.add(l));
    if (reestablishToken) data.collectedTokens.delete(reestablishToken);

    if (!isEstablished) {
      if (data.masterToken) {
        reestablishToken = undefined;
        masterToken = data.masterToken;
        runCallbacks(masterToken === token);

        if (!isEstablished) {
          channel.postMessage({
            collectedTokens,
            masterToken,
            reestablishToken,
          });
        }
        initialEstablishment.resolve();
        isEstablished = true;
      } else if (prevLength !== collectedTokens.size) {
        channel.postMessage({
          collectedTokens,
          masterToken,
          reestablishToken,
        });
      } else {
        reestablishToken = undefined;
        masterToken = Math.max(...Array.from(collectedTokens));
        runCallbacks(masterToken === token);

        if (!isEstablished) {
          channel.postMessage({
            collectedTokens,
            masterToken,
            reestablishToken,
          });
        }
        initialEstablishment.resolve();
        isEstablished = true;
      }
    } else if (!data.masterToken) {
      channel.postMessage({
        collectedTokens,
        masterToken,
        reestablishToken,
      });
    }
  }
};

export function establishMultitabRole(shouldReestablishMasterToSelf?: boolean) {
  if (isChannelClosed) return;
  channel.addEventListener('message', handleMessage);

  channel.postMessage({ collectedTokens });

  // To make the connection faster, we can ignore the waiting and connect right away,
  // and then if we realize we're not master, drop the connection
  setTimeout(() => {
    if (masterToken === undefined) {
      masterToken = token;
      initialEstablishment.resolve();
      runCallbacks(true);
    } else if (shouldReestablishMasterToSelf) {
      reestablishMasterToSelf();
    }
  }, ESTABLISH_TIMEOUT);

  lastWatchdogTickAt = Date.now();
  lastMasterHeartbeatAt = Date.now();
  startMasterWatchdog();

  window.addEventListener('beforeunload', signalTokenDead);
  if (IS_TAURI) window.addEventListener('unload', signalTokenDead);
}

// Watch for the master going away while we are not it. Subscribing to this module's own role callback
// keeps the bookkeeping in step with every path that changes `masterToken`, without threading it
// through each of them. Only a real master→slave transition re-arms the probe: `runCallbacks` also
// fires on non-transitions, and blanking the timestamp there would disable the watchdog for good.
addCallback((isMaster: boolean) => {
  if (isMaster === wasMaster) return;

  wasMaster = isMaster;
  if (isMaster) {
    holdMasterLock();
  } else {
    releaseMasterLock();
    lastMasterHeartbeatAt = Date.now();
    pendingPingAt = 0;
  }
});

// Liveness by question, not by announcement. An interval on the master would prove nothing: Chrome
// throttles a hidden tab's timers to about once a minute, so a perfectly healthy master that happens
// to be in the background would look dead and get deposed every time. A ping is answered from the
// master's message handler, which the browser still runs while its timers are throttled.
function startMasterWatchdog() {
  if (watchdogInterval) return;

  watchdogInterval = window.setInterval(() => {
    // Only a VISIBLE tab takes over: a hidden one is both throttled and the wrong tab to own the socket
    if (masterToken === token || document.visibilityState !== 'visible' || isChannelClosed) return;

    // The machine was asleep (or this tab frozen): every timestamp is stale through no fault of the
    // master. Re-arm and judge on the next round instead of deposing on the first tick after a wake.
    const now = Date.now();
    if (now - lastWatchdogTickAt > WALL_CLOCK_JUMP) {
      lastMasterHeartbeatAt = now;
      pendingPingAt = 0;
      lastWatchdogTickAt = now;
      return;
    }
    lastWatchdogTickAt = now;

    if (pendingPingAt) {
      // A ping went unanswered for the grace period, and nothing arrived from the master in the
      // meantime — take the role. `reestablishMasterToSelf` still asks nicely first.
      if (now - pendingPingAt < MASTER_PING_GRACE) return;

      pendingPingAt = 0;
      if (now - lastMasterHeartbeatAt < MASTER_HEARTBEAT_TIMEOUT) return;

      void takeOverIfMasterGone();
      return;
    }

    if (now - lastMasterHeartbeatAt < MASTER_HEARTBEAT_TIMEOUT) return;

    pendingPingAt = now;
    channel.postMessage({ masterPing: token });
  }, MASTER_HEARTBEAT_INTERVAL);
}

async function takeOverIfMasterGone() {
  const isMasterBusy = masterToken !== undefined
    && Date.now() - lastMasterHeartbeatAt < MASTER_BUSY_TOLERANCE
    && await isMasterLockHeld(masterToken);
  // The master may have answered while the lock was being queried
  if (isMasterBusy || Date.now() - lastMasterHeartbeatAt < MASTER_HEARTBEAT_TIMEOUT) return;

  reestablishMasterToSelf();
}

function holdMasterLock() {
  if (!navigator.locks || masterLock) return;

  masterLock = new Deferred();
  const { promise } = masterLock;
  navigator.locks.request(`${MASTER_LOCK_PREFIX}${token}`, () => promise).catch(() => undefined);
}

function releaseMasterLock() {
  masterLock?.resolve();
  masterLock = undefined;
}

// Without Web Locks there is nothing to ask, so the unanswered ping alone decides, as before
async function isMasterLockHeld(currentMasterToken: number) {
  if (!navigator.locks) return false;

  try {
    const { held } = await navigator.locks.query();
    return Boolean(held?.some(({ name }) => name === `${MASTER_LOCK_PREFIX}${currentMasterToken}`));
  } catch {
    return false;
  }
}

function stopMasterWatchdog() {
  if (!watchdogInterval) return;

  clearInterval(watchdogInterval);
  watchdogInterval = undefined;
}

export function signalTokenDead() {
  if (isChannelClosed) return;
  stopMasterWatchdog();
  runCallbacksTokenDied(token);
  channel.removeEventListener('message', handleMessage);
  channel.postMessage({ tokenDied: token, currentPasscodeHash: getPasscodeHash() });
  channel.close();
  isChannelClosed = true;
}

export function signalPasscodeHash() {
  channel.postMessage({ currentPasscodeHash: getPasscodeHash() });
}

export function getCurrentTabId() {
  return token;
}

export function getAllMultitabTokens() {
  return Array.from(collectedTokens);
}

function claimMasterRole() {
  masterToken = token;
  isWaitingForMaster = false;
  isEstablished = true;
  initialEstablishment.resolve();
  runCallbacks(true);
  // Announce the claim so a concurrent claimant or a stale master reconciles against it (see the
  // `claimedMaster` branch in `handleMessage`): the highest token wins, leaving one acting master.
  channel.postMessage({ claimedMaster: token });
}

export function reestablishMasterToSelf() {
  if (masterToken === token || isWaitingForMaster) return;

  isWaitingForMaster = true;
  channel.postMessage({
    collectedTokens, masterToken: token, shouldGiveUpMaster: true,
  });

  // The hand-over above only completes when the current master answers `hasGaveUpMaster`. A master
  // that is frozen, discarded or otherwise wedged never answers, which is exactly when a takeover is
  // requested — so claim the role after the grace period. `claimMasterRole` broadcasts the claim, and
  // the `claimedMaster` reconciliation guarantees that even when several tabs time out together (a
  // dead master times out every tab's in-flight request at once) they converge on one master instead
  // of splitting. A merely slow master still demotes cooperatively as soon as it catches up.
  setTimeout(() => {
    if (!isWaitingForMaster) return;
    claimMasterRole();
  }, MASTER_HANDOVER_TIMEOUT);
}

export const subscribeToTokenDied = addCallbackTokenDied;
export const subscribeToMasterChange = addCallback;

export const initialEstablishmentPromise = initialEstablishment.promise;

export function isCurrentTabMaster() {
  return masterToken === token;
}
